import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../common';
import {
  CROP_SIZE,
  CropActions,
  CropCanvas,
  CropCircle,
  CropDialog,
  CropDimmer,
  CropImg,
  CropOverlay,
  CropTitle,
  CropViewport,
  CropZoomRow,
  ZoomLabel,
  ZoomSlider,
} from '../styles';

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.05;
const OUTPUT_SIZE = 256;

interface CropState {
  x: number;
  y: number;
  zoom: number;
}

function calcBaseScale(img: HTMLImageElement): number {
  return CROP_SIZE / Math.min(img.naturalWidth, img.naturalHeight);
}

function cropImageToDataURL(
  img: HTMLImageElement,
  crop: CropState,
  baseScale: number,
): string {
  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const ctx = canvas.getContext('2d')!;

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  ctx.beginPath();
  ctx.arc(OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, OUTPUT_SIZE / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

  const ratio = OUTPUT_SIZE / CROP_SIZE;
  const scale = baseScale * crop.zoom;
  const scaledW = img.naturalWidth * scale * ratio;
  const scaledH = img.naturalHeight * scale * ratio;
  const dx = (OUTPUT_SIZE - scaledW) / 2 + crop.x * ratio;
  const dy = (OUTPUT_SIZE - scaledH) / 2 + crop.y * ratio;

  ctx.drawImage(img, dx, dy, scaledW, scaledH);
  return canvas.toDataURL('image/png');
}

interface ImageCropModalProps {
  open: boolean;
  imageSrc: string;
  onClose: () => void;
  onConfirm: (dataUrl: string) => void;
}

export const ImageCropModal: React.FC<ImageCropModalProps> = ({
  open,
  imageSrc,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation(['settings', 'common']);
  const [crop, setCrop] = useState<CropState>({ x: 0, y: 0, zoom: 1 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);
  const baseScaleRef = useRef(1);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setCrop({ x: 0, y: 0, zoom: 1 });
    imgRef.current = null;

    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      baseScaleRef.current = calcBaseScale(img);
      setCrop({ x: 0, y: 0, zoom: 1 });
    };
    img.src = imageSrc;
  }, [open, imageSrc]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setCrop((prev) => ({
      ...prev,
      zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev.zoom - e.deltaY * 0.003)),
    }));
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    setDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setDragStart({ x: e.clientX, y: e.clientY });
      setCrop((prev) => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
    },
    [dragging, dragStart],
  );

  const handlePointerUp = useCallback(() => setDragging(false), []);

  const handleConfirm = () => {
    if (!imgRef.current) return;
    const result = cropImageToDataURL(imgRef.current, crop, baseScaleRef.current);
    onConfirm(result);
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  if (!open) return null;

  const img = imgRef.current;
  const scale = baseScaleRef.current * crop.zoom;
  const scaledW = img ? img.naturalWidth * scale : 0;
  const scaledH = img ? img.naturalHeight * scale : 0;
  const imgX = (CROP_SIZE - scaledW) / 2 + crop.x;
  const imgY = (CROP_SIZE - scaledH) / 2 + crop.y;

  return (
    <CropOverlay onClick={onClose}>
      <CropDialog onClick={(e) => e.stopPropagation()}>
        <CropTitle>{t('settings:profile.cropTitle')}</CropTitle>

        <CropViewport
          ref={containerRef}
          onWheel={handleWheel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <CropCanvas>
            {img && (
              <CropImg
                src={imageSrc}
                alt=""
                draggable={false}
                style={{
                  width: scaledW,
                  height: scaledH,
                  left: imgX,
                  top: imgY,
                }}
              />
            )}
          </CropCanvas>
          <CropCircle />
          <CropDimmer />
        </CropViewport>

        <CropZoomRow>
          <ZoomLabel>{t('settings:profile.zoomOut')}</ZoomLabel>
          <ZoomSlider
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={ZOOM_STEP}
            value={crop.zoom}
            onChange={(e) => setCrop((p) => ({ ...p, zoom: +e.target.value }))}
          />
          <ZoomLabel>{t('settings:profile.zoomIn')}</ZoomLabel>
        </CropZoomRow>

        <CropActions>
          <Button variant="secondary" size="sm" onClick={onClose}>{t('common:button.cancel')}</Button>
          <Button variant="primary" size="sm" onClick={handleConfirm}>{t('settings:profile.apply')}</Button>
        </CropActions>
      </CropDialog>
    </CropOverlay>
  );
};
